import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { InsuranceTypeDto } from '../../../core/data/dtos/insurance-type.dto';
import { BaseCrudService } from '../../../core/data/services/base-crud.service';
import { InsuranceQuestionGroupDto } from '../../../core/data/dtos/insurance-question-group.dto';
import { InsuranceQuestionDto, QuestionType } from '../../../core/data/dtos/insurance-question.dto';
import { AuthService } from '../../auth/services/auth.service';

@Injectable({ providedIn: 'root' })
export class InsuranceTypesService extends BaseCrudService<InsuranceTypeDto> {
  controller = 'InsuranceTypes';
  private readonly authService = inject(AuthService);

  /**
   * Retrieves insurance types from backend.
   * If categoryId is provided, it will be sent as a query parameter.
   * If user is logged in and belongs to a bank, bankId is also sent
   * to filter types (only those without bank OR matching user's bank).
   * Sorting happens server-side; defaults to display priority ascending.
   * Normalizes question types (string → enum) for UI consumption.
   */
  getInsuranceTypes(
    categoryId?: string,
    sortBy: string = 'priority',
    sortDirection: 'asc' | 'desc' = 'asc',
  ): Observable<InsuranceTypeDto[]> {
    const params: Record<string, string> = { sortBy, sortDirection };
    if (categoryId) {
      params['categoryId'] = categoryId;
    }
    // Pass user's bankId if logged in and has one
    const userBankId = this.authService.bankId;
    if (userBankId) {
      params['bankId'] = String(userBankId);
    }
    return this.get<InsuranceTypeDto[]>(params).pipe(
      map((items) => items?.map((it) => this.normalizeInsuranceType(it)) ?? []),
    );
  }

  /**
   * Retrieve a single insurance type by its ID.
   * Normalizes question types (string → enum) for UI consumption.
   */
  getById(id: number): Observable<InsuranceTypeDto> {
    return this.get<InsuranceTypeDto>(undefined, String(id)).pipe(
      map((it) => this.normalizeInsuranceType(it)),
    );
  }

  private normalizeInsuranceType(it: InsuranceTypeDto): InsuranceTypeDto {
    if (!it?.questionGroups?.length) return it;
    const qGroups = (it.questionGroups as InsuranceQuestionGroupDto[])?.map((g) => ({
      ...g,
      questions: [...((g.questions as InsuranceQuestionDto[] | null | undefined) ?? [])]
        // Order configured in the backoffice; the backend already sorts, this is a safety net.
        .sort((a, b) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0) || (a?.id ?? 0) - (b?.id ?? 0))
        .map((q) => ({
          ...q,
          type: this.normalizeQuestionType(q?.type as any),
        })),
    }));
    return { ...it, questionGroups: qGroups };
  }

  private normalizeQuestionType(raw: any): QuestionType | null {
    if (raw == null) return null;

    // Handle numeric values (legacy or backend enum index)
    let numericalValue: number | undefined;
    if (typeof raw === 'number') {
      numericalValue = raw;
    } else if (typeof raw === 'string' && !isNaN(Number(raw)) && raw.trim() !== '') {
      numericalValue = Number(raw);
    }

    if (numericalValue !== undefined) {
      switch (numericalValue) {
        case 0: return QuestionType.Text;
        case 1: return QuestionType.Bool;
        case 2: return QuestionType.File;
        case 3: return QuestionType.Conditional;
        default: return QuestionType.Text;
      }
    }

    // Handle string values
    const s = String(raw).trim().toLowerCase();
    switch (s) {
      case 'text':
        return QuestionType.Text;
      case 'bool':
      case 'boolean':
        return QuestionType.Bool;
      case 'file':
        return QuestionType.File;
      case 'conditional':
        return QuestionType.Conditional;
      default:
        return QuestionType.Text; // safe default
    }
  }
}
